import React from "react";
import _ from "lodash";

import CountrySelector from "../../../common/CountrySelector";
import OSSelector from "../../../common/OSSelector";
import DeviceCategorySelector from "../../../common/DeviceCategorySelector";
import InventoryRegistrySelector from "components/common/InventoryRegistrySelector";
import BrowserSelector from "components/common/BrowserSelector";
import PriceEditModeControl from "./PriceEditModeControl";

import {
  getCatchallPricesLimit,
  getDetectorPricesLimit,
  getXPricesLimit,
  getPricesLimit,
} from "./UPRSegmentationHelper";

const inputClass =
  "bg-white flex w-full border border-gray-400 rounded py-1 px-3 appearance-none leading-normal focus:outline-none focus:border-blue-400 focus:shadow-inner hover:border-gray-500";
const inputClassDisabled =
  "bg-gray-300 flex w-full border border-gray-400 rounded py-1 px-3 appearance-none leading-normal";

const targetingBlockClass = "border p-2 my-2 bg-white";
const targetingTitleClass = "block text-gray-800 font-bold mb-1"; //"font-semibold";

class NetworkUPRSegmentationForm extends React.Component {
  constructor(props) {
    super(props);

    let { rule } = props;
    const {
      id = "",
      name = "",
      optimization = "",
      traffic_scope = "ALL",
      targeting = {},
      detector_prices = [],
      catchall_prices = [],
      x_prices = [],
      prices = [],
    } = rule;

    this.state = {
      id,
      name,
      optimization,
      traffic_scope,
      targeting,
      detector_prices,
      catchall_prices,
      prices,
      x_prices,
      priceValid: [true, true, true, true],
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleOptimizationChanged = this.handleOptimizationChanged.bind(this);
    this.handleTrafficScopeChanged = this.handleTrafficScopeChanged.bind(this);
    this.handleTargetingChanged = this.handleTargetingChanged.bind(this);
    this.handlePricesChanged = this.handlePricesChanged.bind(this);
    this.handleCatchallPricesChanged =
      this.handleCatchallPricesChanged.bind(this);
    this.handleDetectorPricesChanged =
      this.handleDetectorPricesChanged.bind(this);
    this.handleXPricesChanged = this.handleXPricesChanged.bind(this);
    this.handleUpdatePriceValid = this.handleUpdatePriceValid.bind(this);
    this.handleUpdateValid = this.handleUpdateValid.bind(this);
  }

  handleChange(e, key) {
    const value = e.target.value;
    this.setState({
      [key]: value,
    });

    let newRule = this.props.rule;
    newRule[key] = value;
    this.props.handleCurrentRuleChanged(newRule);
  }

  handleOptimizationChanged(newOpt) {
    const { targeting } = this.state;

    if (newOpt === OPTIMIZATION_TYPE.DT) {
      // Optimization: DT => remove inventory registry targeting
      this.handleTargetingChanged(null, TARGETING_TYPE.INVENTORY_REGISTRY);
    } else if (
      _.includes(
        [OPTIMIZATION_TYPE.CST_V2_P, OPTIMIZATION_TYPE.CST_V2_NP],
        newOpt
      ) &&
      !targeting[TARGETING_TYPE.INVENTORY_REGISTRY]
    ) {
      // Optimization: CST_V2_P / CST_V2_NP
      // And Inventory Registry targeting is not set
      // => set inventory registry targeting to empty array
      this.handleTargetingChanged(
        {
          [TARGETING_OP.INCLUDES]: [],
        },
        TARGETING_TYPE.INVENTORY_REGISTRY
      );
    }

    this.setState({
      optimization: newOpt,
    });

    let newRule = this.props.rule;
    newRule.optimization = newOpt;
    this.props.handleCurrentRuleChanged(newRule);
  }

  handleTrafficScopeChanged(trafficScope) {
    this.setState({ traffic_scope: trafficScope });
    let newRule = this.props.rule;
    newRule.traffic_scope = trafficScope;
    this.props.handleCurrentRuleChanged(newRule);
  }

  handleDetectorPricesChanged(prices) {
    this.setState({
      detector_prices: prices,
    });

    let newRule = this.props.rule;
    newRule.detector_prices = prices;
    this.props.handleCurrentRuleChanged(newRule);
  }

  handleCatchallPricesChanged(prices) {
    this.setState({
      catchall_prices: prices,
    });

    // catchall prices is optional, don't save with empty array
    if (_.isEmpty(prices)) {
      let newRule = this.props.rule;
      newRule = _.omit(newRule, "catchall_prices");
      this.props.handleCurrentRuleChanged(newRule);
    } else {
      let newRule = this.props.rule;
      newRule.catchall_prices = prices;
      this.props.handleCurrentRuleChanged(newRule);
    }
  }

  handlePricesChanged(prices) {
    this.setState({
      prices,
    });

    let newRule = this.props.rule;
    newRule.prices = prices;
    this.props.handleCurrentRuleChanged(newRule);
  }

  handleXPricesChanged(prices) {
    this.setState({
      x_prices: prices,
    });

    // catchall prices is optional, don't save with empty array
    if (_.isEmpty(prices)) {
      let newRule = this.props.rule;
      newRule = _.omit(newRule, "x_prices");
      this.props.handleCurrentRuleChanged(newRule);
    } else {
      let newRule = this.props.rule;
      newRule.x_prices = prices;
      this.props.handleCurrentRuleChanged(newRule);
    }
  }

  handleTargetingChanged(newTargeting, key) {
    let { targeting } = this.state;
    targeting[key] = newTargeting;
    if (!newTargeting) {
      targeting = _.omit(targeting, key);
    }

    this.setState({
      targeting,
    });

    let newRule = this.props.rule;
    newRule.targeting = targeting;
    this.props.handleCurrentRuleChanged(newRule);
  }

  handleUpdatePriceValid(idx, flag) {
    const { priceValid } = this.state;
    priceValid[idx] = flag;
    this.setState({ priceValid });
  }

  handleCheckTargetValid() {
    const { optimization, targeting } = this.state;
    if (optimization === OPTIMIZATION_TYPE.DT) {
      return !targeting[TARGETING_TYPE.INVENTORY_REGISTRY];
    } else if (
      optimization === OPTIMIZATION_TYPE.CST_V2_NP ||
      optimization === OPTIMIZATION_TYPE.CST_V2_P
    ) {
      return (
        targeting[TARGETING_TYPE.INVENTORY_REGISTRY] &&
        targeting[TARGETING_TYPE.INVENTORY_REGISTRY][TARGETING_OP.INCLUDES] &&
        targeting[TARGETING_TYPE.INVENTORY_REGISTRY][TARGETING_OP.INCLUDES]
          .length > 0
      );
    } else if (optimization === OPTIMIZATION_TYPE.CST_V1) {
      return true;
    }
  }

  handleUpdateValid() {
    const { id, name, optimization, targeting, priceValid } = this.state;
    if (id === "") {
      return "ID is required";
    }
    if (name === "") {
      return "Name is required";
    }

    if (optimization === "") {
      return "Optimization is required";
    }

    if (!targeting || _.isEmpty(targeting)) {
      return "Targeting is required";
    }

    if (!this.handleCheckTargetValid()) {
      return "Targeting Inventory Registry is invalid";
    }

    if (!priceValid[0]) {
      return "Prices is invalid";
    }

    if (!priceValid[1]) {
      return "Detector Prices is invalid";
    }
    if (!priceValid[2]) {
      return "X Prices is invalid";
    }
    if (!priceValid[3]) {
      return "Catchall Prices is invalid";
    }

    return "";
  }

  render() {
    const { networkInfo, modalType } = this.props;
    const {
      id,
      name,
      optimization,
      traffic_scope,
      targeting,
      detector_prices,
      x_prices,
      prices,
      catchall_prices,
    } = this.state;

    return (
      <div>
        <div className="rounded mb-4 bg-white px-8 pb-8 pt-6">
          <div className="mb-4">
            <label className="block font-bold text-gray-800" htmlFor="ruleId">
              ID
            </label>
            <input
              className={modalType === "ADD" ? inputClass : inputClassDisabled}
              id="ruleId"
              type="number"
              value={id}
              onChange={(e) => this.handleChange(e, "id")}
              disabled={modalType !== "ADD"}
            />
          </div>

          <div className="mb-4">
            <label className="block font-bold text-gray-800" htmlFor="name">
              Name
            </label>
            <input
              className={inputClass}
              id="name"
              type="text"
              value={name}
              onChange={(e) => this.handleChange(e, "name")}
            />
          </div>

          <div className="border-t rounded mb-4 bg-gray-300 p-2">
            <div className="border-b mt-2 text-lg font-bold">Optimization</div>
            <div>
              <OptimizationControl
                isReadonly={modalType !== "ADD"}
                optimization={optimization}
                handleOptimizationChanged={this.handleOptimizationChanged}
              ></OptimizationControl>
            </div>
          </div>

          <div className="border-t rounded mb-4 bg-gray-300 p-2">
            <div className="border-b mt-2 text-lg font-bold">Traffic Scope</div>
            <div>
              <TrafficScopeControl
                isReadonly={modalType !== "ADD"}
                trafficScope={traffic_scope}
                handleChanged={this.handleTrafficScopeChanged}
              ></TrafficScopeControl>
            </div>
          </div>

          <div className="border-t rounded mb-4 bg-gray-300 p-2">
            <div className="border-b mt-2 text-lg font-bold">Targeting</div>

            <div className={targetingBlockClass}>
              <div className={targetingTitleClass}>OS</div>
              <TargetingWithInExControl
                isReadonly={modalType !== "ADD"}
                targetingType={TARGETING_TYPE.OS}
                targeting={targeting[TARGETING_TYPE.OS]}
                handleTargetingChanged={(t) =>
                  this.handleTargetingChanged(t, TARGETING_TYPE.OS)
                }
              ></TargetingWithInExControl>
            </div>

            <div className={targetingBlockClass}>
              <div className={targetingTitleClass}>Device Category</div>

              <TargetingWithInExControl
                isReadonly={modalType !== "ADD"}
                targetingType={TARGETING_TYPE.DC}
                targeting={targeting[TARGETING_TYPE.DC]}
                handleTargetingChanged={(t) =>
                  this.handleTargetingChanged(t, TARGETING_TYPE.DC)
                }
              ></TargetingWithInExControl>
            </div>

            <div className={targetingBlockClass}>
              <div className={targetingTitleClass}>Country</div>
              <TargetingWithInExControl
                isReadonly={modalType !== "ADD"}
                targetingType={TARGETING_TYPE.COUNTRY}
                targeting={targeting[TARGETING_TYPE.COUNTRY]}
                handleTargetingChanged={(t) =>
                  this.handleTargetingChanged(t, TARGETING_TYPE.COUNTRY)
                }
              ></TargetingWithInExControl>
            </div>

            <div className={targetingBlockClass}>
              <div className={targetingTitleClass}>Browser</div>
              <TargetingWithInExControl
                isReadonly={modalType !== "ADD"}
                targetingType={TARGETING_TYPE.BROWSER}
                targeting={targeting[TARGETING_TYPE.BROWSER]}
                handleTargetingChanged={(t) =>
                  this.handleTargetingChanged(t, TARGETING_TYPE.BROWSER)
                }
              ></TargetingWithInExControl>
            </div>

            {optimization !== OPTIMIZATION_TYPE.DT && (
              <div className={targetingBlockClass}>
                <div className={targetingTitleClass}>Inventory Registry</div>
                <TargetingWithInExControl
                  key={`inventory_registry_${optimization}`}
                  isReadonly={modalType !== "ADD"}
                  targetingType={TARGETING_TYPE.INVENTORY_REGISTRY}
                  targeting={targeting[TARGETING_TYPE.INVENTORY_REGISTRY]}
                  handleTargetingChanged={(t) =>
                    this.handleTargetingChanged(
                      t,
                      TARGETING_TYPE.INVENTORY_REGISTRY
                    )
                  }
                ></TargetingWithInExControl>
              </div>
            )}
          </div>

          <div className="border-t rounded mb-4 bg-gray-300 p-2">
            <div className="border-b mt-2 text-lg font-bold">
              Prices in {networkInfo.currency}
            </div>
            <PriceEditModeControl
              prices={prices}
              limit={getPricesLimit({ optimization })}
              handleSync={this.handlePricesChanged}
              handleValidChanged={(flag) =>
                this.handleUpdatePriceValid(0, flag)
              }
            ></PriceEditModeControl>
          </div>

          <div className="border-t rounded mb-4 bg-gray-300 p-2">
            <div className="border-b mt-2 text-lg font-bold">
              Detector Prices in {networkInfo.currency}
            </div>
            <PriceEditModeControl
              prices={detector_prices}
              limit={getDetectorPricesLimit({ optimization })}
              handleSync={this.handleDetectorPricesChanged}
              handleValidChanged={(flag) =>
                this.handleUpdatePriceValid(1, flag)
              }
            ></PriceEditModeControl>
          </div>

          <div className="border-t rounded mb-4 bg-gray-300 p-2">
            <div className="border-b mt-2 text-lg font-bold">
              Catchall Prices
            </div>
            <PriceEditModeControl
              prices={catchall_prices}
              limit={getCatchallPricesLimit({ optimization })}
              handleSync={this.handleCatchallPricesChanged}
              handleValidChanged={(flag) =>
                this.handleUpdatePriceValid(3, flag)
              }
            ></PriceEditModeControl>
          </div>

          <div className="border-t rounded mb-4 bg-gray-300 p-2">
            <div className="border-b mt-2 text-lg font-bold">
              Experiment (X) Prices in {networkInfo.currency}
            </div>
            <PriceEditModeControl
              prices={x_prices}
              limit={getXPricesLimit({ optimization })}
              handleSync={this.handleXPricesChanged}
              handleValidChanged={(flag) =>
                this.handleUpdatePriceValid(2, flag)
              }
            ></PriceEditModeControl>
          </div>
        </div>
      </div>
    );
  }
}

const TRAFFIC_SCOPE_ENUM = {
  ALL: "ALL", // default
  NO_PREBID: "NO_PREBID",
};
class TrafficScopeControl extends React.PureComponent {
  constructor(props) {
    super(props);

    const { trafficScope } = this.props;

    this.state = {
      trafficScope,
    };

    this.handleTrafficScopeChanged = this.handleTrafficScopeChanged.bind(this);
  }

  // radioValue
  handleTrafficScopeChanged(trafficScope) {
    this.setState({ trafficScope });
    this.props.handleChanged(trafficScope);
  }

  render() {
    const { isReadonly } = this.props;
    const { trafficScope } = this.state;

    return (
      <div>
        <div className="mb-4">
          <div className="flex items-center gap-4">
            {_.values(TRAFFIC_SCOPE_ENUM).map((value) => {
              return (
                <label
                  key={value}
                  htmlFor={`radio_traffic_scope_${value}`}
                  className="cursor-pointer"
                >
                  <input
                    id={`radio_traffic_scope_${value}`}
                    type="radio"
                    name={`radio_traffic_scope`}
                    value={value}
                    checked={trafficScope === value}
                    onChange={() => this.handleTrafficScopeChanged(value)}
                    disabled={isReadonly}
                  />{" "}
                  {value}
                </label>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
}

const OPTIMIZATION_TYPE = {
  DT: "dt",
  CST_V1: "cstv1",
  CST_V2_P: "cstv2p",
  CST_V2_NP: "cstv2np",
};
class OptimizationControl extends React.PureComponent {
  constructor(props) {
    super(props);

    const { optimization } = this.props;

    this.state = {
      optimization,
    };

    this.handleOptTypeChanged = this.handleOptTypeChanged.bind(this);
  }

  // radioValue
  handleOptTypeChanged(optType) {
    this.setState({ optimization: optType });
    this.props.handleOptimizationChanged(optType);
  }

  render() {
    const { isReadonly } = this.props;
    const { optimization } = this.state;

    return (
      <div>
        <div className="mb-4">
          {/* <div className="block text-gray-800 font-bold">Optimization</div> */}
          <div className="flex items-center gap-4">
            {_.keys(OPTIMIZATION_TYPE).map((key) => {
              const value = OPTIMIZATION_TYPE[key];
              return (
                <label
                  key={key}
                  htmlFor={`radio_opt_type_${key}`}
                  className="cursor-pointer"
                >
                  <input
                    id={`radio_opt_type_${key}`}
                    type="radio"
                    name={`radio_optimization`}
                    value={value}
                    checked={optimization === value}
                    onChange={() => this.handleOptTypeChanged(value)}
                    disabled={isReadonly}
                  />{" "}
                  {value}
                </label>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
}

// Important! snake case!!! camelCase is in customUPR
const TARGETING_TYPE = {
  OS: "os",
  DC: "device_category",
  COUNTRY: "country",
  BROWSER: "browser",
  INVENTORY_REGISTRY: "inventory_registry",
};

const TARGETING_OP = {
  NONE: "NONE",
  INCLUDES: "includes",
  EXCLUDES: "excludes",
};

// type: country, os, device_category, hour
class TargetingWithInExControl extends React.PureComponent {
  constructor(props) {
    super(props);
    // targetingType
    // targeting.includes
    // handleTargetingChanged

    const { targeting } = props;

    let radioValue = TARGETING_OP.NONE;
    let selectedItems = [];
    if (targeting) {
      if (targeting.includes) {
        radioValue = TARGETING_OP.INCLUDES;
        selectedItems = targeting.includes;
      } else {
        radioValue = TARGETING_OP.EXCLUDES;
        selectedItems = targeting.excludes;
      }
    }

    this.state = {
      radioValue,
      selectedItems,
    };

    this.handleChangeRadio = this.handleChangeRadio.bind(this);
    this.handleItemsChanged = this.handleItemsChanged.bind(this);
  }

  handleChangeRadio(value) {
    this.setState({
      radioValue: value,
    });

    if (value === TARGETING_OP.NONE) {
      this.props.handleTargetingChanged(null);
      this.setState({ selectedItems: [] });
    } else {
      const items = this.state.selectedItems;
      if (_.isEmpty(items)) {
        this.props.handleTargetingChanged(null);
      } else {
        let tt = {
          [value]: items,
        };
        this.props.handleTargetingChanged(tt);
      }
    }
  }

  handleItemsChanged(items) {
    const { radioValue } = this.state;
    // console.log("items changed", items, radioValue);
    if (radioValue !== TARGETING_OP.NONE) {
      if (_.isEmpty(items)) {
        this.props.handleTargetingChanged(null);
      } else {
        let tt = {
          [radioValue]: items,
        };
        this.props.handleTargetingChanged(tt);
      }
    }
  }

  render() {
    // type: country, os, device_category, hour
    const { targetingType, isReadonly } = this.props;
    const { radioValue, selectedItems } = this.state;

    let TargetingSelector = "";
    switch (targetingType) {
      case TARGETING_TYPE.DC: {
        TargetingSelector = (
          <DeviceCategorySelector
            selectedItems={selectedItems}
            handleChanged={this.handleItemsChanged}
            isReadonly={isReadonly}
          ></DeviceCategorySelector>
        );
        break;
      }

      case TARGETING_TYPE.OS: {
        TargetingSelector = (
          <OSSelector
            selectedItems={selectedItems}
            handleChanged={this.handleItemsChanged}
            isReadonly={isReadonly}
          ></OSSelector>
        );
        break;
      }

      case TARGETING_TYPE.COUNTRY: {
        TargetingSelector = (
          <CountrySelector
            selectedItems={selectedItems}
            handleChanged={this.handleItemsChanged}
            isReadonly={isReadonly}
          ></CountrySelector>
        );
        break;
      }

      case TARGETING_TYPE.BROWSER: {
        TargetingSelector = (
          <BrowserSelector
            selectedItems={selectedItems}
            handleChanged={this.handleItemsChanged}
            isReadonly={isReadonly}
          ></BrowserSelector>
        );
        break;
      }

      case TARGETING_TYPE.INVENTORY_REGISTRY: {
        TargetingSelector = (
          <InventoryRegistrySelector
            selectedItems={selectedItems}
            handleChanged={this.handleItemsChanged}
            isReadonly={isReadonly}
          ></InventoryRegistrySelector>
        );
        break;
      }

      default: {
        TargetingSelector = "Selector not implemented";
      }
    }

    const isExcludesDisabled =
      targetingType === TARGETING_TYPE.INVENTORY_REGISTRY;

    return (
      <>
        <div className="flex items-center gap-4">
          <label
            htmlFor={`${targetingType}_radio_none`}
            className="cursor-pointer"
          >
            <input
              id={`${targetingType}_radio_none`}
              type="radio"
              name={`${targetingType}_radio`}
              value={TARGETING_OP.NONE}
              checked={radioValue === TARGETING_OP.NONE}
              onChange={() => this.handleChangeRadio(TARGETING_OP.NONE)}
              disabled={isReadonly}
            />{" "}
            None
          </label>

          <label
            htmlFor={`${targetingType}_radio_include`}
            className="cursor-pointer"
          >
            <input
              id={`${targetingType}_radio_include`}
              type="radio"
              name={`${targetingType}_radio`}
              value={TARGETING_OP.INCLUDES}
              checked={radioValue === TARGETING_OP.INCLUDES}
              onChange={() => this.handleChangeRadio(TARGETING_OP.INCLUDES)}
              disabled={isReadonly}
            />{" "}
            Includes
          </label>

          <label
            htmlFor={`${targetingType}_radio_exclude`}
            className={
              isExcludesDisabled
                ? "cursor-not-allowed text-gray-500"
                : "cursor-pointer"
            }
          >
            <input
              id={`${targetingType}_radio_exclude`}
              type="radio"
              name={`${targetingType}_radio`}
              value={TARGETING_OP.EXCLUDES}
              checked={radioValue === TARGETING_OP.EXCLUDES}
              onChange={() => this.handleChangeRadio(TARGETING_OP.EXCLUDES)}
              disabled={isReadonly || isExcludesDisabled}
            />{" "}
            Excludes
          </label>
        </div>

        {radioValue !== TARGETING_OP.NONE && TargetingSelector}
      </>
    );
  }
}

export default NetworkUPRSegmentationForm;
